/**
 * The component stylesheet both surfaces paint with.
 *
 * `tokensToCss` (in @clawharbor/manifest) emits the tenant's --ch-* custom
 * properties; this file emits the rules that CONSUME them. The split matters:
 * the widget layers the MCP host's theme variables over ours, and the WebMCP
 * page swaps tokens live, so the rules must never bake a tenant colour in.
 *
 * Every declaration is `var(--ch-thing, <literal>)`. The literal is filled from
 * the brand object when one is supplied, so a stylesheet served without its
 * token block still looks like the tenant rather than like nothing.
 */
import type { Brand } from '@clawharbor/manifest';
import { tokensToCss, googleFontHref, normalizeBrandTokens } from '@clawharbor/manifest';
import { contrastRatio, contrastSafeOverrides } from './contrast.js';

// tokensToCss is the manifest's job, not ours. Re-exported so surface code has a
// single import site for "everything CSS".
export { tokensToCss, googleFontHref };

export interface StylesheetOpts {
  /** Collapse whitespace and drop comments. The widget wants this; the page does not. */
  minify?: boolean;
  /** Prefix every rule, e.g. '.ch-scope', when embedding inside a hostile host page. */
  scope?: string;
}

type PaletteLike = Record<string, { value?: string } | undefined>;

/** `var(--ch-role, literal)` where the literal is the tenant's own value if known. */
function color(brand: Brand | undefined, role: string, fallback: string): string {
  const value = (brand?.palette as PaletteLike | undefined)?.[role]?.value ?? fallback;
  return `var(--ch-${role.replace(/\./g, '-')}, ${value})`;
}

function space(brand: Brand | undefined, i: number, fallback: number): string {
  const value = brand?.density?.scale?.[i] ?? fallback;
  return `var(--ch-space-${i}, ${value}px)`;
}

function radius(brand: Brand | undefined, name: string, fallback: number): string {
  const value = brand?.shape?.radius?.[name] ?? fallback;
  return `var(--ch-radius-${name}, ${value}px)`;
}

function font(brand: Brand | undefined, kind: 'sans' | 'display', fallback: string): string {
  const fam = kind === 'sans' ? brand?.typography?.families?.sans : brand?.typography?.families?.display;
  return `var(--ch-font-${kind}, ${fam?.stack ?? fallback})`;
}

function size(brand: Brand | undefined, step: string, fallback: number): string {
  const value = brand?.typography?.scale?.steps?.[step] ?? fallback;
  return `var(--ch-size-${step}, ${value}px)`;
}

/**
 * Crude but safe: only whitespace and comments are touched, and no space around
 * `-` or inside strings is collapsed, so `calc()` and font stacks survive.
 */
export function minifyCss(css: string): string {
  return css
    .replace(/\/\*[\s\S]*?\*\//g, '')
    .replace(/\s+/g, ' ')
    .replace(/\s*([{};,>])\s*/g, '$1')
    .replace(/;\}/g, '}')
    .replace(/:\s+/g, ':')
    .trim();
}

/**
 * Component CSS for the render-core vocabulary. ~5KB unminified; the whole point
 * is that it is small enough to inline in a widget without thinking about it.
 */
export function baseStylesheet(raw?: Brand, opts: StylesheetOpts = {}): string {
  const p = opts.scope ? `${opts.scope} ` : '';
  // The same normalisation `tokensToCss` applies to the tokens. Every literal
  // below is the fallback for a token that may not have been emitted, so reading
  // the raw brand here would bake the 3px padding back in as the fallback of the
  // variable that was just corrected.
  const brand = raw ? normalizeBrandTokens(raw).brand : undefined;

  const fgDefault = color(brand, 'fg.default', '#111111');
  const fgMuted = color(brand, 'fg.muted', '#5c5c5c');
  const bgCanvas = color(brand, 'bg.canvas', '#ffffff');
  const bgSurface = color(brand, 'bg.surface', '#ffffff');
  const bgRaised = color(brand, 'bg.raised', '#f4f4f4');
  const border = color(brand, 'border.default', '#e6e6e6');
  const primary = color(brand, 'brand.primary', '#1a56db');
  const primaryHover = color(brand, 'brand.primaryHover', primary);
  const onPrimary = color(brand, 'brand.onPrimary', '#ffffff');
  const onBrand = color(brand, 'fg.onBrand', onPrimary);
  const premium = color(brand, 'accent.premium', primary);
  const danger = color(brand, 'state.danger', '#b3261e');
  const success = color(brand, 'state.success', '#1e7d32');

  // `var()` cannot ask whether it is legible, so the choice is made here, from
  // the same brand object the tokens are emitted from.
  const primaryValue = (brand?.palette as PaletteLike | undefined)?.['brand.primary']?.value;
  const canvasValue = (brand?.palette as PaletteLike | undefined)?.['bg.canvas']?.value;
  const primaryRatio = contrastRatio(primaryValue, canvasValue);
  const ring = primaryRatio != null && primaryRatio < 3 ? fgDefault : primary;

  const sans = font(brand, 'sans', 'system-ui, sans-serif');
  const display = font(brand, 'display', sans);

  const s1 = space(brand, 1, 4);
  const s2 = space(brand, 2, 8);
  const s3 = space(brand, 3, 12);
  const s4 = space(brand, 4, 16);
  const s5 = space(brand, 5, 24);

  const rCard = radius(brand, 'card', 8);
  const rControl = radius(brand, 'control', 8);
  const rChip = radius(brand, 'chip', 999);
  const bw = `var(--ch-border-width, ${brand?.shape?.borderWidth ?? 1}px)`;

  const duration = `var(--ch-motion-duration, ${brand?.motion?.durationMs ?? 180}ms)`;
  const easing = `var(--ch-motion-easing, ${brand?.motion?.easing ?? 'cubic-bezier(.2,.8,.2,1)'})`;

  const sizeMd = size(brand, 'md', 16);
  const sizeSm = size(brand, 'sm', 14);
  const sizeXs = size(brand, 'xs', 12);
  const sizeLg = size(brand, 'lg', 20);

  const css = `
/* ---- root -------------------------------------------------------------- */
${p}.ch-root {
  font-family: ${sans};
  font-size: var(--ch-font-size-base, ${brand?.typography?.baseSizePx ?? 16}px);
  font-weight: var(--ch-font-weight-base, ${brand?.typography?.baseWeight ?? 400});
  line-height: 1.45;
  color: ${fgDefault};
  background: ${bgCanvas};
  padding: ${s4};
  box-sizing: border-box;
  container-type: inline-size;
}
${p}.ch-root *, ${p}.ch-root *::before, ${p}.ch-root *::after { box-sizing: inherit; }
/* A ring in the tenant's primary disappears the moment the primary is close to
   the page's own ground — and a keyboard user then has no idea where they are.
   So the colour is measured against the canvas and drops to the body ink when it
   would not be seen, and the ring is two-tone: a band of the canvas between the
   element and the ring keeps it visible over a card, a badge or a photo. */
${p}.ch-root :focus-visible {
  outline: 2px solid ${ring};
  outline-offset: 2px;
  box-shadow: 0 0 0 2px ${bgCanvas};
  border-radius: ${rControl};
}
${p}.ch-sr {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip-path: inset(50%); white-space: nowrap; border: 0;
}

/* ---- header ------------------------------------------------------------ */
${p}.ch-header {
  display: flex; flex-wrap: wrap; align-items: baseline; justify-content: space-between;
  gap: ${s2}; margin: 0 0 ${s3};
}
${p}.ch-title {
  font-family: ${display};
  font-weight: var(--ch-font-weight-heading, ${brand?.typography?.headingWeight ?? 700});
  font-size: ${sizeLg};
  text-transform: var(--ch-case-heading, ${brand?.typography?.case?.heading ?? 'none'});
  letter-spacing: var(--ch-tracking-heading, 0);
  margin: 0;
}
${p}.ch-subtitle { color: ${fgMuted}; font-size: ${sizeSm}; margin: ${s1} 0 0; }

/* ---- freshness chip ---------------------------------------------------- */
${p}.ch-chip {
  display: inline-flex; align-items: center; gap: ${s1};
  padding: 2px ${s2}; border-radius: ${rChip};
  border: ${bw} solid ${border};
  background: ${bgRaised};
  color: ${fgMuted};
  font-size: ${sizeXs};
  white-space: nowrap;
}
${p}.ch-chip__dot { width: 7px; height: 7px; border-radius: 999px; background: currentColor; flex: none; }
${p}.ch-chip--live { color: ${success}; }
${p}.ch-chip--cached { color: ${fgMuted}; }
${p}.ch-chip--stale, ${p}.ch-chip--fixture { color: ${danger}; border-color: currentColor; }

/* ---- disclaimer -------------------------------------------------------- */
${p}.ch-disclaimer {
  display: block; margin: 0 0 ${s3}; padding: ${s2} ${s3};
  border-left: 3px solid ${premium};
  border-radius: ${rControl};
  background: ${bgRaised};
  color: ${fgMuted};
  font-size: ${sizeXs};
  line-height: 1.4;
}
${p}.ch-disclaimer a { color: inherit; text-underline-offset: 2px; }

/* ---- contrast warning -------------------------------------------------- */
${p}.ch-warn {
  margin: 0 0 ${s3}; padding: ${s2} ${s3};
  border: ${bw} dashed ${danger};
  border-radius: ${rControl};
  color: ${danger};
  font-size: ${sizeXs};
}
${p}.ch-warn ul { margin: ${s1} 0 0; padding-left: ${s4}; }

/* ---- groups ------------------------------------------------------------ */
${p}.ch-group { margin: 0 0 ${s4}; }
${p}.ch-group__title {
  font-family: ${display};
  font-size: ${sizeSm};
  font-weight: var(--ch-font-weight-heading, 700);
  text-transform: var(--ch-case-heading, none);
  color: ${fgMuted};
  margin: 0 0 ${s2};
  padding-bottom: ${s1};
  border-bottom: ${bw} solid ${border};
}

/* ---- list / grid ------------------------------------------------------- */
${p}.ch-list, ${p}.ch-grid, ${p}.ch-timeline { list-style: none; margin: 0; padding: 0; }
${p}.ch-list { display: flex; flex-direction: column; gap: ${s2}; }
${p}.ch-grid {
  display: grid; gap: ${s2};
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
}
${p}.ch-card {
  display: flex; flex-direction: column; gap: ${s1};
  padding: ${s3};
  background: ${bgSurface};
  border: ${bw} solid ${border};
  border-radius: ${rCard};
  box-shadow: var(--ch-shadow-card, none);
  transition: border-color ${duration} ${easing}, transform ${duration} ${easing};
}
${p}.ch-card:hover { border-color: ${primaryHover}; }
${p}.ch-card__title { font-weight: 600; font-size: ${sizeMd}; margin: 0; }
${p}.ch-card__title a { color: inherit; text-decoration: none; }
${p}.ch-card__title a:hover { text-decoration: underline; text-underline-offset: 2px; }
${p}.ch-card__sub { color: ${fgDefault}; font-size: ${sizeSm}; margin: 0; }
${p}.ch-card__meta { color: ${fgMuted}; font-size: ${sizeXs}; margin: 0; }
${p}.ch-card__actions { display: flex; flex-wrap: wrap; gap: ${s2}; margin-top: ${s1}; }
${p}.ch-card.is-disabled { opacity: .55; }

/* ---- badges ------------------------------------------------------------ */
${p}.ch-badges { display: flex; flex-wrap: wrap; gap: ${s1}; margin: 0; padding: 0; list-style: none; }
${p}.ch-badge {
  display: inline-block; padding: 1px ${s2};
  border-radius: ${rChip};
  background: ${primary};
  color: ${onBrand};
  font-size: ${sizeXs};
  font-weight: 600;
  line-height: 1.6;
  white-space: nowrap;
}
${p}.ch-badge--muted { background: ${bgRaised}; color: ${fgMuted}; border: ${bw} solid ${border}; }
${p}.ch-badge--premium { background: ${premium}; color: ${onBrand}; }
${p}.ch-badge--danger { background: transparent; color: ${danger}; border: ${bw} solid ${danger}; }

/* ---- buttons ----------------------------------------------------------- */
${p}.ch-btn {
  display: inline-flex; align-items: center; gap: ${s1};
  padding: ${s1} ${s3};
  border-radius: ${rControl};
  border: ${bw} solid transparent;
  font: inherit;
  font-size: ${sizeSm};
  font-weight: 600;
  text-transform: var(--ch-case-button, ${brand?.typography?.case?.button ?? 'none'});
  text-decoration: none;
  cursor: pointer;
  background: ${primary};
  color: ${onPrimary};
  transition: background ${duration} ${easing};
}
${p}.ch-btn:hover { background: ${primaryHover}; }
${p}.ch-btn--ghost { background: transparent; color: ${fgDefault}; border-color: ${border}; }
${p}.ch-btn--ghost:hover { background: ${bgRaised}; }
${p}.ch-btn[aria-disabled='true'] { pointer-events: none; opacity: .5; }

/* ---- table ------------------------------------------------------------- */
${p}.ch-table-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; }
${p}.ch-table { width: 100%; border-collapse: collapse; font-size: ${sizeSm}; }
${p}.ch-table caption { text-align: left; color: ${fgMuted}; font-size: ${sizeXs}; padding-bottom: ${s2}; }
${p}.ch-table th, ${p}.ch-table td {
  text-align: left; padding: ${s2} ${s3};
  border-bottom: ${bw} solid ${border};
  vertical-align: top;
}
${p}.ch-table thead th {
  color: ${fgMuted}; font-size: ${sizeXs}; font-weight: 600;
  text-transform: var(--ch-case-heading, none);
  position: sticky; top: 0; background: ${bgCanvas};
}
${p}.ch-table tbody tr:hover { background: ${bgRaised}; }

/* ---- timeline ---------------------------------------------------------- */
${p}.ch-timeline { display: flex; flex-wrap: wrap; gap: ${s2}; }
${p}.ch-timeline > li { display: flex; }
${p}.ch-slot--time {
  font-family: ${display};
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  font-size: ${sizeMd};
}
${p}.ch-perf {
  display: flex; flex-direction: column; align-items: center; gap: 2px;
  min-width: 72px; padding: ${s2} ${s3};
  background: ${bgSurface};
  border: ${bw} solid ${border};
  border-radius: ${rControl};
  color: ${fgDefault};
  text-decoration: none;
  transition: border-color ${duration} ${easing}, background ${duration} ${easing};
}
${p}.ch-perf:hover { border-color: ${primary}; background: ${bgRaised}; }
${p}.ch-perf .ch-card__meta { text-align: center; }
${p}.ch-perf.is-disabled { opacity: .45; pointer-events: none; text-decoration: line-through; }

/* ---- detail ------------------------------------------------------------ */
${p}.ch-detail {
  background: ${bgSurface};
  border: ${bw} solid ${border};
  border-radius: ${rCard};
  padding: ${s4};
}
${p}.ch-dl { display: grid; grid-template-columns: minmax(6rem, 12rem) 1fr; gap: ${s1} ${s3}; margin: ${s3} 0 0; }
${p}.ch-dl dt { color: ${fgMuted}; font-size: ${sizeXs}; }
${p}.ch-dl dd { margin: 0; font-size: ${sizeSm}; }

/* ---- empty ------------------------------------------------------------- */
${p}.ch-empty {
  margin: 0; padding: ${s5} ${s4};
  text-align: center;
  color: ${fgMuted};
  background: ${bgRaised};
  border: ${bw} dashed ${border};
  border-radius: ${rCard};
  font-size: ${sizeSm};
}

@container (max-width: 380px) {
  ${p}.ch-grid { grid-template-columns: 1fr; }
  ${p}.ch-dl { grid-template-columns: 1fr; }
}
@media (prefers-reduced-motion: reduce) {
  ${p}.ch-root * { transition: none !important; animation: none !important; }
}
`.trim();

  return opts.minify ? minifyCss(css) : css;
}

/**
 * Everything a surface needs in one string: tenant tokens, optional contrast
 * repairs, then the component rules. Order matters — rules must come last so a
 * host's later override still wins over ours but not over the tokens.
 */
export function fullStylesheet(
  brand: Brand,
  opts: StylesheetOpts & { selector?: string; contrastSafe?: boolean } = {},
): string {
  const selector = opts.selector ?? ':root';
  const parts = [tokensToCss(brand, selector)];
  if (opts.contrastSafe) parts.push(contrastSafeOverrides(brand, selector));
  parts.push(baseStylesheet(brand, opts));
  const css = parts.filter(Boolean).join('\n\n');
  return opts.minify ? minifyCss(css) : css;
}
